import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { 
  CheckCircle2, Circle, AlertTriangle, Loader2, 
  ChevronDown, ChevronUp, Shield
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MilestoneStatusPanel Component
 * 
 * Read-only display of milestone completion status for an engagement.
 * No override controls - milestones are system-validated only.
 * 
 * Props:
 * - serId: Service Execution Record ID
 */
const MilestoneStatusPanel = ({ serId }) => {
  const { token } = useAuth();
  const [milestones, setMilestones] = useState([]);
  const [summary, setSummary] = useState({ completed: 0, total: 0, percentage: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchMilestones = async () => {
      if (!serId || !token) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/engagements/${serId}/milestones`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch milestone status');
        }
        
        const data = await response.json();
        
        if (data.ok) {
          setMilestones(data.milestones || []);
          setSummary(data.completion_summary || { completed: 0, total: 0, percentage: 0 });
        }
      } catch (err) {
        console.error("Failed to load milestones:", err);
        setError("Unable to load milestone status");
      } finally {
        setLoading(false);
      }
    };

    fetchMilestones();
  }, [serId, token]);

  if (loading) {
    return (
      <div className="glass p-6 rounded-xl" data-testid="milestone-status-panel">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
          <span className="text-slate-400">Loading milestone status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass p-6 rounded-xl" data-testid="milestone-status-panel">
        <div className="flex items-center gap-3 text-amber-400">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="glass p-6 rounded-xl" data-testid="milestone-status-panel">
      {/* Header */}
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-semibold text-white">Engagement Validation Status</h3>
            <p className="text-sm text-slate-400">
              {summary.completed} of {summary.total} milestones complete ({summary.percentage}%)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Progress bar */}
          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${summary.percentage}%` }}
            />
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </div>

      {/* Expanded milestone list */}
      {expanded && (
        <div className="mt-6 space-y-3">
          {milestones.map((milestone) => (
            <div 
              key={milestone.milestone_id}
              className={`p-4 rounded-lg border transition-colors ${
                milestone.completed 
                  ? 'border-green-500/30 bg-green-950/20' 
                  : 'border-slate-700 bg-slate-800/50'
              }`}
              data-testid={`milestone-${milestone.milestone_id}`}
            >
              <div className="flex items-start gap-3">
                {milestone.completed ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <Circle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className={`font-medium text-sm ${
                      milestone.completed ? 'text-green-400' : 'text-slate-300'
                    }`}>
                      {formatMilestoneId(milestone.milestone_id)}
                    </h4>
                    {milestone.completed && milestone.completed_at && (
                      <span className="text-xs text-slate-500">
                        {formatDate(milestone.completed_at)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mt-1">
                    {milestone.description}
                  </p>
                  
                  {/* Missing evidence (only show if not completed) */}
                  {!milestone.completed && milestone.missing_evidence && milestone.missing_evidence.length > 0 && (
                    <div className="mt-2 text-xs text-amber-400/80">
                      <span className="font-medium">Missing: </span>
                      {milestone.missing_evidence.slice(0, 3).join(", ")}
                      {milestone.missing_evidence.length > 3 && ` +${milestone.missing_evidence.length - 3} more`}
                    </div>
                  )}
                  
                  {/* Validation method badge */}
                  <div className="mt-2">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${
                      milestone.validation_method === 'SYSTEM' 
                        ? 'bg-blue-900/50 text-blue-400'
                        : milestone.validation_method === 'HYBRID'
                        ? 'bg-purple-900/50 text-purple-400'
                        : 'bg-amber-900/50 text-amber-400'
                    }`}>
                      {milestone.validation_method}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Legend (when expanded) */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-slate-700/50">
          <p className="text-xs text-slate-500">
            Milestones are validated by the system based on evidence collected. 
            No manual overrides are available.
          </p>
        </div>
      )}
    </div>
  );
};

// Helper functions
function formatMilestoneId(id) {
  // M1_SERVICE_INITIALIZED -> Service Initialized
  return id
    .replace(/^M\d+_/, '')
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function formatDate(dateString) {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
}

export default MilestoneStatusPanel;
